/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { memo } from 'preact/compat';
import { SelectionDetail } from '../UNSAFE_Collection';
import { Selector } from '../UNSAFE_Selector';
import { CurrentRowVariant, TableSelectionDetail } from '../UNSAFE_TableView';
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey, isSameKey } from '../utils/UNSAFE_keys';
import { TableColumn } from './Table';
import { TableCell } from './TableCell';
import { TableRowVariantOptions } from './themes/TableRowStyles.css';
import { TableRowRedwoodTheme } from './themes/redwood/TableRowTheme';
import { BundleType } from '../resources/nls/bundle';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';

/**
 * Props for the TableRow Component
 */
export type TableRowProps<K, D, C> = {
  tableId: string;
  rowKey: K;
  rowData: D;
  rowIndex: number;
  isFinalRow: boolean;
  columnsArray: TableColumn<K, D, C>[];
  rowHeaderColumnKeys: Set<C>;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isRowSelectionEnabled: boolean;
  isSingleRowSelectionEnabled: boolean;
  isRtl: boolean;
  selectedRowKeys?: Keys<K>;
  focusRingColumnKey?: C;
  selectedColumnKeys?: Keys<C>;
  variant?: CurrentRowVariant;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  startStickyEdge?: number;
  endStickyEdge?: number;
};

/**
 * The internal component used to render a single row in Table.
 */
function TableRow<K, D, C>({
  tableId,
  rowKey,
  rowData,
  rowIndex,
  isFinalRow,
  columnsArray,
  rowHeaderColumnKeys,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  isSelected,
  isPreviousSelected,
  isRowSelectionEnabled,
  isSingleRowSelectionEnabled,
  selectedRowKeys = { all: false, keys: new Set<K>() },
  focusRingColumnKey,
  selectedColumnKeys = { all: false, keys: new Set<C>() },
  variant,
  onSelectionChange,
  startStickyEdge,
  endStickyEdge,
  isRtl
}: TableRowProps<K, D, C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle();

  const { classes: themeClasses } = useComponentTheme<TableRowVariantOptions>(
    TableRowRedwoodTheme,
    { lastRow: isFinalRow ? 'isLastRow' : 'notLastRow' }
  );

  const classes = classNames([themeClasses]);

  const accessibleRowName = (rowIndex + 2).toString();
  const handleRowSelectorChange = (detail: SelectionDetail<K>) => {
    if (onSelectionChange != null) {
      onSelectionChange(
        { value: { row: detail.value, column: selectedColumnKeys }, target: detail.target },
        false
      );
    }
  };
  const selectorRenderer =
    isRowSelectionEnabled && !isSingleRowSelectionEnabled
      ? () => {
          // conversion from 0-based index and adding the header row as it is always rendered
          return (
            <Selector
              aria-label={translations.collection_selectRow({ ROW_NAME: accessibleRowName })}
              onChange={handleRowSelectorChange}
              rowKey={rowKey}
              selectedKeys={selectedRowKeys}
            />
          );
        }
      : undefined;

  let isPreviousColumnSelected = false;
  return (
    <tr
      aria-rowindex={rowIndex + 1}
      class={classes}
      role={'row'}
      data-oj-key={rowKey}
      data-oj-table-data-row={tableId}
      {...(isRowSelectionEnabled
        ? isSelected
          ? { 'aria-selected': true }
          : { 'aria-selected': false }
        : {})}
      {...(typeof rowKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...(isRowSelectionEnabled && interactionProps)}>
      {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
        const columnKey = column.key;
        const isColumnSelected = containsKey(selectedColumnKeys, columnKey);
        const cellProps = {
          tableId: tableId,
          rowData: rowData,
          field: column.value.field,
          stickyColumn: column.value.stickyEdge,
          columnKey: columnKey,
          columnIndex: columnIndex,
          rowKey: rowKey,
          rowIndex: rowIndex,
          isFinalRowIndex: isFinalRow,
          isFinalColumnIndex: columnIndex === columnsArray.length - 1,
          hasVerticalGridlines: hasVerticalGridlines,
          hasHorizontalGridlines: hasHorizontalGridlines,
          isRowSelected: isSelected,
          isPreviousRowSelected: isPreviousSelected,
          isColumnSelected: isColumnSelected,
          isPreviousColumnSelected: isPreviousColumnSelected,
          isShowFocusRing: column.key === focusRingColumnKey,
          isRowHeader: rowHeaderColumnKeys.has(column.key),
          renderer: column.value.renderer,
          selectorRenderer: selectorRenderer,
          isRowSelectionEnabled: isRowSelectionEnabled,
          variant: variant,
          isRowSingleSelection: isSingleRowSelectionEnabled,
          applyActiveStyle: applyActiveStyle,
          applyHoverStyle: applyHoverStyle,
          applyPseudoHoverStyle: applyPseudoHoverStyle,
          isStickyStartEdge: columnIndex === startStickyEdge,
          isStickyEndEdge: columnIndex === endStickyEdge,
          isRtl: isRtl
        };
        isPreviousColumnSelected = isColumnSelected;
        return <TableCell {...cellProps} />;
      })}
    </tr>
  );
}

const _areStringSetsEquivalent = (set1?: Set<string>, set2?: Set<string>) => {
  if (set1 == null) {
    return set2 == null;
  }
  if (set2 == null) {
    return false;
  }
  return set1.size === set2.size && [...set1].every((x) => set2.has(x));
};

const _areColumnsArraysEquivalent = (
  columnsArray1: TableColumn<string | number, any, string>[],
  columnsArray2: TableColumn<string | number, any, string>[]
) => {
  if (columnsArray1.length === columnsArray2.length) {
    for (let i = 0; i < columnsArray1.length; i++) {
      if (
        columnsArray1[i].key !== columnsArray2[i].key ||
        columnsArray1[i].value !== columnsArray2[i].value
      ) {
        return false;
      }
    }
    return true;
  }
  return false;
};

export const MemoizeTableRow = memo(TableRow, (prev: any, next: any) => {
  if (prev && next) {
    return (
      prev.tableId === next.tableId &&
      prev.rowKey === next.rowKey &&
      prev.rowData === next.rowData &&
      prev.rowIndex === next.rowIndex &&
      prev.isFinalRow === next.isFinalRow &&
      _areColumnsArraysEquivalent(prev.columnsArray, next.columnsArray) &&
      prev.horizontalGridlines === next.horizontalGridlines &&
      prev.verticalGridlines === next.verticalGridlines &&
      _areStringSetsEquivalent(prev.rowHeaderColumnKeys, next.rowHeaderColumnKeys) &&
      prev.isSelected === next.isSelected &&
      prev.isPreviousSelected === next.isPreviousSelected &&
      prev.isRowSelectionEnabled === next.isRowSelectionEnabled &&
      prev.isSingleRowSelectionEnabled === next.isSingleRowSelectionEnabled &&
      (prev.isRowSelectionEnabled && !prev.isSingleRowSelectionEnabled
        ? isSameKey(prev.selectedRowKeys, next.selectedRowKeys)
        : true) &&
      isSameKey(prev.selectedColumnKeys, next.selectedColumnKeys) &&
      prev.focusRingColumnKey === next.focusRingColumnKey &&
      prev.variant === next.variant &&
      prev.onSelectionChange === next.onSelectionChange &&
      prev.startStickyEdge === next.startStickyEdge &&
      prev.endStickyEdge === next.endStickyEdge &&
      prev.isRtl === next.isRtl
    );
  }
  return false;
});
